import React, { FunctionComponent, ReactNode, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Accordion, AccordionActions, AccordionDetails, AccordionSummary, Alert, Box, Checkbox, Chip, IconButton, ListItem, ListItemButton, ListItemText, Snackbar, TextField, Tooltip, Typography } from "@mui/material";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import WarningIcon from '@mui/icons-material/WarningOutlined';

import { List } from "react-window";

const PRIMARY_COLOR = "#1976d2";
const ACCORDION_WIDTH = "20vw";
const ACCORDION_MIN_WIDTH = "300px";
const ACCORDION_MAX_WIDTH = "600px";

export interface FilterAccordionItem<T> {
    id: string | number;
    label: string;
    data: T;
    selected?: boolean;
    disabled?: boolean;
    showChip?: boolean;
    canDeleteChip?: boolean;
}

export interface FilterAccordionChipsProps<T> {
    minWidthPerChip?: number | string;
    maxWidthPerChip?: number | string;
    widthPerChip?: number | string;
    maxPerRow?: number;
    maxRows?: number;
}

export interface FilterAccordionProps<T> {
    title: string;
    items: FilterAccordionItem<T>[];
    expanded: boolean;
    disabled?: boolean;
    allowMultipleSelection?: boolean;
    showSearch?: boolean;
    showCheckbox?: boolean;
    maxContentHeight?: number;
    canSelectAll?: boolean;
    minWidth?: number | string;
    width?: number | string;
    maxWidth?: number | string;
    chipsProps?: FilterAccordionChipsProps<T>;
    maxSelectedItems?: number;
    onToggle: () => void;
    onItemClick: (item: FilterAccordionItem<T>["id"], items: FilterAccordionItem<T>[]) => void;
    onHeaderHeightChange?: (height: number) => void;
}

const accordionSlots = {

};

const accordionSlotProps = {
    transition: {
        unmountOnExit: true,
        timeout: 100,
    },
};

const FilterAccordionInner = <T,>(props: FilterAccordionProps<T>) => {
    const { title, items, expanded, disabled, allowMultipleSelection, showSearch, showCheckbox, maxSelectedItems, maxContentHeight, minWidth, maxWidth, width, canSelectAll, chipsProps = {}, onToggle, onItemClick, onHeaderHeightChange } = props;
    const [searchTerm, setSearchTerm] = useState("");
    const [filteredItems, setFilteredItems] = useState(items);
    const containerRef = useRef<HTMLDivElement>(null);
    const headerRef = useRef<HTMLDivElement>(null);
    const lastHeaderHeightRef = useRef<number | null>(null);
    const [headerHeight, setHeaderHeight] = useState<number | null>(null);
    const [showMaxSelectedWarning, setShowMaxSelectedWarning] = useState(false);
    const [maxSelectedWarningContent, setMaxSelectedWarningContent] = useState("");

    const getSelectedItems = useCallback(() => {
        return items.filter(i => i.selected);
    }, [items]);

    const selectedItems = useMemo(() => getSelectedItems(), [getSelectedItems]);
    const selectedItemsRef = useRef(selectedItems);


    const handleItemClick = (item: FilterAccordionItem<T>) => {
        // If current item was selected and max selected items is defined, update all selected items to not be higher than max selected items - 1 to allow unselecting current item
        let updatedItems: FilterAccordionItem<T>[];
        if (item.selected && maxSelectedItems) {
            const selectedItems = items.filter(i => i.selected);
            if (selectedItems.length > maxSelectedItems) {
                const selectedItemsExcludingCurrent = selectedItems.filter(i => i.id !== item.id);
                const itemsToDeselect = selectedItemsExcludingCurrent.slice(maxSelectedItems);
                updatedItems = items.map(i => {
                    if (i.id === item.id) {
                        return { ...i, selected: false };
                    }
                    if (itemsToDeselect.some(d => d.id === i.id)) {
                        return { ...i, selected: false };
                    }
                    return i;
                });
            } else {
                updatedItems = items.map(i => i.id === item.id ? { ...i, selected: false } : i);
            }
        } else {
            updatedItems = items.map(i => i.id === item.id ? { ...i, selected: !i.selected } : i);
        }

        onItemClick(item.id, updatedItems);
    };

    useEffect(() => {
        if (!searchTerm) {
            setFilteredItems(items);
            return;
        }
        const lowerSearchTerm = searchTerm.toLowerCase();
        setFilteredItems(items.filter(item => item.label.toLowerCase().includes(lowerSearchTerm)));
    }, [items, searchTerm]);

    useEffect(() => {
        if (selectedItems.length === selectedItemsRef.current.length && selectedItems.every((item, idx) => item.id === selectedItemsRef.current[idx].id && item.selected === selectedItemsRef.current[idx].selected)) {
            return;
        }

        selectedItemsRef.current = selectedItems;

        const allSelected = selectedItems.length === items.length;
        if (allSelected && canSelectAll && allowMultipleSelection) {
            setShowMaxSelectedWarning(false);
            return;
        }

        if (maxSelectedItems && selectedItems.length >= maxSelectedItems) {
            const msg = `Tout sélectionner ou sélectionner jusqu'à ${maxSelectedItems} éléments`;
            setMaxSelectedWarningContent(msg);  // (`Vous ne pouvez sélectionner que jusqu'à ${maxSelectedItems} ${title.toLowerCase()}${canSelectAll ? ' ou tous les éléments' : ''}. Les éléments supplémentaires ne seront pas pris en compte.`);
            setShowMaxSelectedWarning(true);
        }
    }, [selectedItems, maxSelectedItems]);

    useLayoutEffect(() => {
        if (!headerRef.current) return;

        const resizeObserverHandler = (entries: ResizeObserverEntry[]) => {
            for (const entry of entries) {
                const height = Math.round(entry.contentRect.height);
                if (lastHeaderHeightRef.current !== height) {
                    lastHeaderHeightRef.current = height;
                    setHeaderHeight(height);
                    if (onHeaderHeightChange) onHeaderHeightChange(height);
                }
            }
        }
        const resizeObserver = new ResizeObserver(resizeObserverHandler);

        resizeObserver.observe(headerRef.current);

        return () => {
            resizeObserver.disconnect();
        };
    }, []);

    const renderChips = () => {
        const selectedItems = getSelectedItems();
        if (selectedItems.length === 0) return null;

        const allSelected = selectedItems.length === items.length;

        if (allSelected) {
            return (
                <Chip
                    label="Tous"
                    size="small"
                    onDelete={canSelectAll && allowMultipleSelection ? () => onItemClick("all", items.map(i => ({ ...i, selected: false }))) : undefined}
                    sx={{
                        height: "1.2rem",
                        fontSize: "0.75rem",
                        width: "fit-content",
                        maxWidth: "fit-content",
                        '& .MuiChip-label': {
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                        },
                        '& .MuiSvgIcon-root': {
                            height: "0.8rem",
                            width: "0.8rem",
                        }
                    }}
                    color="primary"
                />
            );
        }

        const MAX_PER_ROW = chipsProps.maxPerRow || 3;
        const MAX_ROWS = chipsProps.maxRows || 1;
        const maxVisible = MAX_PER_ROW * MAX_ROWS;
        const minWidthPerChip = chipsProps.minWidthPerChip || `20%`;
        const maxWidthPerChip = chipsProps.maxWidthPerChip || `30%`;
        const widthPerChip = chipsProps.widthPerChip || undefined;

        const displayable = selectedItems.filter(item => item.showChip !== false);

        const hasMore = selectedItems.length > maxVisible;

        if (displayable.length <= maxVisible) {
            return (
                <Box
                    display="flex"
                    flexWrap="wrap"
                    width="100%"
                    gap="4px"
                    overflow="hidden"
                    sx={{ maxHeight: '2.6rem' }}
                >
                    {displayable.map((item, idx) => {
                        const warningSelectedMaxItems = !hasMore && ((idx + 1) === maxVisible || (idx + 1) === selectedItems.length) && maxSelectedItems && selectedItems.length >= maxSelectedItems;
                        return (
                            <Tooltip key={item.id} title={item.label} placement="top">
                                <Chip
                                    key={item.id}
                                    label={item.label}
                                    size="small"
                                    onDelete={item.canDeleteChip === false ? undefined : () => handleItemClick(item)}
                                    sx={{
                                        height: "1.2rem",
                                        fontSize: "0.75rem",
                                        width: widthPerChip || "fit-content",
                                        minWidth: minWidthPerChip,
                                        maxWidth: maxWidthPerChip,
                                        '& .MuiChip-label': {
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                        },
                                        '& .MuiSvgIcon-root': {
                                            height: "0.8rem",
                                            width: "0.8rem",
                                        }
                                    }}
                                    color={warningSelectedMaxItems ? "warning" : "primary"}
                                    avatar={warningSelectedMaxItems ? <WarningIcon style={{ fontSize: "0.8rem", color: "#fff" }} /> : undefined}
                                />
                            </Tooltip>
                        )
                    })}
                </Box>
            );
        }

        const visibleCount = maxVisible - 1;
        const visibleItems = displayable.slice(0, visibleCount);
        const remaining = displayable.length - visibleCount;
        const remainingItems = displayable.slice(visibleCount);
        const warningSelectedMaxItems = maxSelectedItems && selectedItems.length >= maxSelectedItems;
        return (
            <Box
                display="flex"
                flexWrap="wrap"
                width="100%"
                gap="4px"
                overflow="hidden"
                sx={{ maxHeight: '2.6rem' }}
            >
                {visibleItems.map(item => (
                    <Tooltip key={item.id} title={item.label} placement="top">
                        <Chip
                            key={item.id}
                            label={item.label}
                            size="small"
                            onDelete={item.canDeleteChip === false ? undefined : () => handleItemClick(item)}
                            sx={{
                                height: "1.2rem",
                                fontSize: "0.75rem",
                                width: widthPerChip || "fit-content",
                                minWidth: minWidthPerChip,
                                maxWidth: maxWidthPerChip,
                                '& .MuiChip-label': {
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                },
                                '& .MuiSvgIcon-root': {
                                    height: "0.8rem",
                                    width: "0.8rem",
                                }
                            }}
                            color="primary"
                        />
                    </Tooltip>
                ))}

                <Tooltip
                    title={(
                        <Box
                            onClick={(e) => {
                                e.stopPropagation();
                            }}
                            sx={{
                                overflowY: "auto",
                                maxHeight: "50vh",
                                p: 1,
                            }}
                        >
                            {remainingItems.map(item => (
                                <Box key={item.id} display="flex" alignItems="center" justifyContent="space-between" gap="4px" mb="2px">
                                    <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>{item.label}</Typography>
                                    {item.canDeleteChip === false ? null : (
                                        <IconButton size="small" onClick={() => handleItemClick(item)} sx={{ color: 'white', p: 0 }}>
                                            <CloseIcon style={{
                                                fontSize: "0.8rem",
                                            }} />
                                        </IconButton>
                                    )}
                                </Box>
                            ))}
                        </Box>
                    )}

                >
                    <Chip
                        key="more"
                        label={`+${remaining}`}
                        size="small"
                        sx={{
                            height: "1.2rem",
                            fontSize: "0.75rem",
                            width: typeof widthPerChip === "string" ? `calc(${widthPerChip} - 10%)` : (widthPerChip! - 10) || "fit-content",
                        }}
                        color={warningSelectedMaxItems ? "warning" : "primary"}
                        avatar={warningSelectedMaxItems ? <WarningIcon style={{ fontSize: "0.8rem", color: "#fff" }} /> : undefined}
                    />
                </Tooltip>
            </Box>
        );
    };

    const renderSearch = () => {
        return (
            <TextField
                size="small"
                placeholder={`Rechercher ${title.toLowerCase()}`}
                fullWidth
                sx={{
                    padding: "0.25rem 0.5rem",
                    boxSizing: "border-box",
                    "& .MuiInputBase-root-MuiOutlinedInput-root": {
                        paddingLeft: 0,
                    },
                    "& .MuiInputBase-input": {
                        padding: "0.25rem 0.25rem 0.25rem 0",
                        fontSize: "0.85rem",
                    }
                }}
                InputProps={{
                    startAdornment: <SearchIcon fontSize="small" sx={{ color: 'text.secondary', mr: 0.5, }} />,
                    style: { paddingLeft: "0.5rem", paddingRight: searchTerm ? 0 : "0.5rem" },
                    endAdornment: searchTerm ? (
                        <IconButton size="small" onClick={() => setSearchTerm("")} sx={{ color: 'text.secondary', mr: 0.5, p: 0 }}>
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    ) : null,
                }}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
            />
        );
    };

    return (
        <>
            <Accordion
                ref={containerRef}
                defaultExpanded={expanded}
                expanded={expanded}
                disableGutters
                variant="outlined"
                onChange={onToggle}
                disabled={disabled}
                sx={{
                    width: width || ACCORDION_WIDTH,
                    minWidth: minWidth || ACCORDION_MIN_WIDTH,
                    maxWidth: maxWidth || ACCORDION_MAX_WIDTH,
                }}
                slotProps={accordionSlotProps}
            >
                <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    ref={headerRef}
                    sx={{
                        height: headerHeight ? `${headerHeight}px` : 'auto',
                        transition: 'height 180ms ease',
                        overflow: 'hidden',
                        padding: 0,
                        marginLeft: "1rem",
                    }}
                >
                    <Box
                        display="flex"
                        alignItems="center"
                        width="100%"
                        overflow="hidden"
                        flexWrap="nowrap"
                        gap="4px"
                    >
                        <Typography
                            fontWeight={"bold"}
                            variant="subtitle2"
                            sx={{ flexShrink: 0, mr: 1 }}
                        >
                            {title} ({items.length})
                        </Typography>
                        {renderChips()}
                    </Box>
                </AccordionSummary>
                <AccordionDetails
                    sx={{
                        p: 0,
                        maxHeight: maxContentHeight,
                        overflow: "hidden",
                        display: 'flex',
                        flexDirection: 'column',
                    }}

                >
                    <AccordionActions
                        sx={{
                            padding: 0,
                        }}
                    >
                        {showSearch && renderSearch()}
                    </AccordionActions>

                    <List
                        rowHeight={36}
                        rowCount={filteredItems.length + (canSelectAll && allowMultipleSelection ? 1 : 0)}
                        overscanCount={5}
                        rowProps={{}}
                        rowComponent={(props) => {
                            const selectAllOffset = canSelectAll && allowMultipleSelection ? 1 : 0;

                            // "Select All" row
                            if (selectAllOffset && props.index === 0) {
                                const allSelected = items.length > 0 && items.every(i => i.selected);
                                return (
                                    <ListItem disableGutters disablePadding style={props.style}>
                                        <ListItemButton
                                            disableGutters
                                            selected={allSelected}
                                            onClick={() => {
                                                const allSelected = items.length > 0 && items.every(i => i.selected);
                                                onItemClick("all", items.map(i => ({ ...i, selected: !allSelected })));
                                            }}
                                            sx={{
                                                "&.MuiListItemButton-root": {
                                                    backgroundColor: (showCheckbox === false && allSelected) ? PRIMARY_COLOR : "transparent",
                                                    padding: "0.25rem 0.5rem",
                                                    color: (showCheckbox === false && allSelected) ? "white" : "inherit",
                                                    "&:hover": {
                                                        backgroundColor: (showCheckbox === false && allSelected) ? "#1565c0" : "#f5f5f5",
                                                    },
                                                },
                                                ".MuiTypography-root": {
                                                    fontSize: "0.85rem",
                                                }
                                            }}
                                        >
                                            <Checkbox
                                                size="small"
                                                checked={allSelected}
                                                sx={{ p: 0, mr: 1 }}
                                            />
                                            <ListItemText primary={allSelected ? "Tout déselectionner" : "Tout sélectionner"} />
                                        </ListItemButton>
                                    </ListItem>
                                );
                            }

                            const itemIndex = props.index - selectAllOffset;
                            if (itemIndex >= filteredItems.length) return null;

                            const { style } = props;
                            const item = filteredItems[itemIndex];
                            const isSelected = !!item.selected;
                            const disabled = !!item.disabled || (maxSelectedItems ? !isSelected && getSelectedItems().length >= maxSelectedItems : false);

                            return (
                                <ListItem
                                    disableGutters
                                    disablePadding
                                    key={item.id}
                                    style={{
                                        ...style,
                                        paddingLeft: "1rem",
                                    }}
                                >
                                    <ListItemButton
                                        disableGutters
                                        selected={isSelected}
                                        disabled={disabled}
                                        onClick={() => {
                                            if (item.disabled) return;
                                            handleItemClick(item);
                                        }}
                                        sx={{
                                            "&.MuiListItemButton-root": {
                                                backgroundColor: (!showCheckbox && item.selected) ? PRIMARY_COLOR : "transparent",
                                                padding: "0.25rem 0.5rem",
                                                color: (!showCheckbox && item.selected) ? "white" : "inherit",
                                                "&:hover": {
                                                    backgroundColor: (!showCheckbox && item.selected) ? "#1565c0" : "#f5f5f5",
                                                },
                                            },
                                            ".MuiTypography-root": {
                                                fontSize: "0.85rem",
                                            }
                                        }}
                                    >
                                        {showCheckbox && (
                                            <Checkbox
                                                size="small"
                                                checked={isSelected}
                                                sx={{
                                                    p: 0,
                                                    mr: 1,
                                                }}
                                            />
                                        )}
                                        <ListItemText primary={item.label} />
                                    </ListItemButton>
                                </ListItem>
                            );
                        }}
                    />
                </AccordionDetails>
            </Accordion>
            {showMaxSelectedWarning && (
                <SnackbarAlert content={maxSelectedWarningContent} onClose={() => setShowMaxSelectedWarning(false)} />
            )}
        </>
    );
};

interface SnackbarAlertProps {
    content: ReactNode;
    onClose?: () => void;
}

export const SnackbarAlert: FunctionComponent<SnackbarAlertProps> = ({ content, onClose }) => {
    const [open, setOpen] = useState(true);

    if (!open) return null;

    return (
        <Snackbar
            open={open}
            autoHideDuration={10000}
            onClose={() => {
                setOpen(false);
                if (onClose) onClose();
            }}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            sx={{
                zIndex: 99999,
            }}
        >
            <Alert
                severity="warning"
                variant="filled"
                sx={{ width: '100%' }}
                onClose={() => {
                    setOpen(false);
                    if (onClose) onClose();
                }}
            >
                {content}
            </Alert>
        </Snackbar>
    );
};

export const FilterAccordion = React.memo(FilterAccordionInner) as typeof FilterAccordionInner;

export default FilterAccordion;
